import { useEffect, useState } from "react";
import { usersApi, type User } from "../api/client";
import { PageHeader } from "../components/PageHeader";
import {
  AI_PROVIDER_OPTIONS,
  AI_PROVIDERS,
  getStoredAiProvider,
  setStoredAiProvider,
  type AiProvider,
} from "../lib/ai";
import { todayLocalDate } from "../lib/date";

export function SettingsPage({
  user,
  onLogout,
  onUserChanged,
}: {
  user: User;
  onLogout: () => void;
  onUserChanged: (date?: string) => Promise<User | null>;
}) {
  const [name, setName] = useState(user.name);
  const [target, setTarget] = useState(String(user.daily_target_kcal));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [aiProvider, setAiProvider] = useState<AiProvider | null>(null);
  const [aiSavedAt, setAiSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setName(user.name);
    setTarget(String(user.daily_target_kcal));
  }, [user.name, user.daily_target_kcal]);

  useEffect(() => {
    setAiProvider(getStoredAiProvider());
  }, []);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersApi.update({
        name: name.trim() || undefined,
        daily_target_kcal: Number(target) || undefined,
        target_date: todayLocalDate(),
      });
      await onUserChanged(todayLocalDate());
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const onSelectAiProvider = (provider: AiProvider) => {
    setStoredAiProvider(provider);
    setAiProvider(provider);
    setAiSavedAt(Date.now());
  };

  return (
    <div className="page">
      <PageHeader title="設定" titleClassName="page-title-half" />
      <form onSubmit={onSave}>
        <div className="field">
          <label htmlFor="name">表示名</label>
          <input id="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="target">1日の目標カロリー (kcal)</label>
          <input
            id="target"
            inputMode="numeric"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </div>
        <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>
          今日ここで変えた目標は今日分として保存され、過去日の目標はそのまま残ります。
        </p>
        <button className="btn-primary" disabled={saving}>
          保存する
        </button>
        {savedAt && <p className="muted" style={{ marginTop: 8 }}>保存しました</p>}
      </form>

      <hr className="hairline" style={{ margin: "var(--space-xl) 0" }} />

      <section>
        <h2 className="section-title">AI連携</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          AIにカロリーを聞くボタンで開く既定のサービスを選びます。
        </p>
        <div className="settings-choice-grid">
          {AI_PROVIDER_OPTIONS.map((provider) => (
            <button
              key={provider}
              type="button"
              className={aiProvider === provider ? "btn-primary settings-choice-button" : "btn-pearl settings-choice-button"}
              onClick={() => onSelectAiProvider(provider)}
            >
              {AI_PROVIDERS[provider].label}
            </button>
          ))}
        </div>
        <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
          現在: {aiProvider ? AI_PROVIDERS[aiProvider].label : "未設定"}
        </p>
        {aiSavedAt && <p className="muted" style={{ marginTop: 8 }}>AI設定を保存しました</p>}
      </section>

      <hr className="hairline" style={{ margin: "var(--space-xl) 0" }} />

      <div className="list-item">
        <div className="list-item-main">
          <span className="list-item-title">{user.email}</span>
          <span className="list-item-sub">ログイン中</span>
        </div>
        <button className="btn-link" onClick={onLogout}>
          ログアウト
        </button>
      </div>
    </div>
  );
}
